import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Box, VStack } from '@web/components/atoms/Layout'
import {
  DonationBackHeader,
  ExtraFields,
  TermsAndConditions,
  useDonationFormContext,
} from '@web/components/page-components/DonationForm'
import { PageButton } from '@web/components/page-components/PageButton'
import { useEffect, useState } from 'react'
import { Body2 } from '@web/components/atoms/Typography'
import { DonationFormStepProps, EXCLUDED_FIELDS, useInstallmentAmount, useTotalAmountWithDonorPays } from './utils'
import { toDisplayAmount } from '@web/components/utils/format'
import { usePaymentStore } from '../../../libs/hooks/usePaymentStore'
import { CustomFieldElementType, DonationFrequency, ExtraFieldType, PaymentMethodType } from '@web/graphql-components'
import { PaymentIntentResult, StripeError } from '@stripe/stripe-js'
import { useUpdatePaymentObjectDetailsMutation } from '../../../libs/payment.queries'
import { UpdatePaymentDetailsInput } from '@web/graphql-queries/graphql'
import DonationStepTabs from '../../../../../packages/components/src/lib/page-components/DonationForm/components/atoms/DonationStepTab'
import NameForm from '@web/components/page-components/DonationForm/components/molecules/NameForm'
import { Nullable } from 'apps/fe-donation/utils'
import ProfileSelector from './ProfileSelector'
import { DONATION_FREQUENCY_TO_LABEL } from '@web/components/page-components/DonationForm/donationUtils'
import { usePurchaseCopy } from '@web/components/page-components/DonationForm/purchaseCopy'

const PAYMENT_METHOD_LABEL_MAP: Partial<Record<PaymentMethodType, string>> = {
  [PaymentMethodType.CreditCard]: 'Card',
  [PaymentMethodType.DirectDebit]: 'Direct Debit',
}

const DonationCheckoutForm = ({
  paymentMethodType,
  extraFields,
  legalUrl,
  withAccordion = false,
  hasBackButton = true,
}: DonationFormStepProps & {
  paymentMethodType: PaymentMethodType
  withAccordion?: boolean
  hasBackButton?: boolean
}) => {
  const stripe = useStripe()
  const stripeElements = useElements()

  const formValues = useDonationFormContext((state) => state.formValues)
  const actions = useDonationFormContext((state) => state.actions)
  const settings = useDonationFormContext((state) => state.settings)
  const isThreeStepForm = useDonationFormContext((state) => state.settings.styles?.steps === 3)

  const paymentIntentId = usePaymentStore(({ paymentIntentId }) => paymentIntentId)
  const subscriptionId = usePaymentStore(({ subscriptionId }) => subscriptionId)

  const { givingFrequency = DonationFrequency.OneTime, isPayByInstallments } = formValues
  const purchaseCopy = usePurchaseCopy()
  const totalAmount = useTotalAmountWithDonorPays()
  const installmentAmount = useInstallmentAmount()

  const { nextStep } = actions

  const [hasStripeLoaded, setHasStripeLoaded] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState<Nullable<string>>('')

  const { mutateAsync: updatePaymentObjectDetails } = useUpdatePaymentObjectDetailsMutation()
  useEffect(() => {
    if (!stripeElements) {
      return
    }

    stripeElements.getElement('payment')!.on('ready', () => {
      setHasStripeLoaded(true)
    })
  }, [stripeElements])

  const handleStripeError = (error: StripeError) => {
    setMessage(
      error.type === 'card_error' || error.type === 'validation_error'
        ? error.message
        : 'An unexpected error occurred.',
    )
  }

  const handleCreditCardConfirm = async (clientSecret: string, paymentMethodId: string | undefined) => {
    return await stripe!.confirmPayment({
      clientSecret,
      confirmParams: {
        payment_method: paymentMethodId,
        return_url: window.location.href,
      },
      redirect: 'if_required',
    })
  }

  const handleDirectDebitConfirm = async (clientSecret: string, paymentMethodId: string | undefined) => {
    return await stripe!.confirmAuBecsDebitPayment(clientSecret, {
      payment_method: paymentMethodId,
    })
  }

  const handleSubmit = async () => {
    if (!stripe || !stripeElements) {
      return
    }

    setIsSubmitting(true)

    try {
      const { error: submitError } = await stripeElements.submit()
      if (submitError) {
        handleStripeError(submitError)
        return
      }
      const { firstName, lastName, organisation, mobile, address, comment, isAnonymous, email, ...customFields } =
        formValues?.extraFields ?? {}

      const { error: paymentIntentError, paymentMethod } = await stripe.createPaymentMethod({
        elements: stripeElements,
        params: {
          billing_details: {
            name: `${firstName?.trim()} ${lastName?.trim()}`,
            email: email?.trim(),
            address: {
              country: 'AU',
            },
          },
        },
      })

      let newPaymentMethodType = paymentMethodType

      if (paymentMethod?.card?.wallet?.apple_pay) {
        newPaymentMethodType = PaymentMethodType.ApplePay
      } else if (paymentMethod?.card?.wallet?.google_pay) {
        newPaymentMethodType = PaymentMethodType.GooglePay
      }

      const updatePaymentIntentInput: UpdatePaymentDetailsInput = {
        firstName: firstName?.trim(),
        lastName: lastName?.trim(),
        organisation: organisation?.trim(),
        mobile: mobile?.trim(),
        address: formValues?.address,
        fullAddress: address,
        paymentCardType: paymentMethod?.card?.brand,
        paymentMethodId: paymentMethod?.id,
        paymentIntentId,
        subscriptionId,
        givingFrequency: givingFrequency === DonationFrequency.OneTime ? givingFrequency : undefined,
        paymentMethodType: newPaymentMethodType,
        customFields: Object.entries(customFields)
          .map(([key, value]: [string, string]) => {
            // find customfield setting by key
            // check customfield type and if multi select, split the value by comma
            const customField = settings.extraFields.find((field) => field.customField?.id === key)
            console.log(`Processing custom field ${customField?.name} with value: ${value}`)

            let valueToSend = [value?.trim() ?? '']
            if (
              customField?.type === ExtraFieldType.Custom &&
              customField.customField?.elementType === CustomFieldElementType.MultiSelect
            ) {
              console.log(`Parsing multi-select field ${customField.name} with value: ${value}`)
              const updatedValues = valueToSend[0].split(',')

              return {
                customFieldSettingId: key,
                name: (customField?.name ?? '')?.trim(),
                values: updatedValues,
              }
            }

            return {
              customFieldSettingId: key,
              name: (customField?.name ?? '')?.trim(),
              values: valueToSend,
            }
          })
          .filter(({ name }) => !EXCLUDED_FIELDS.includes(name)),
      }

      const res = await updatePaymentObjectDetails(updatePaymentIntentInput)

      if (paymentIntentError) {
        handleStripeError(paymentIntentError)
      }

      let result: PaymentIntentResult | null = null
      const { clientSecret } = usePaymentStore.getState()

      switch (paymentMethodType) {
        case PaymentMethodType.DirectDebit:
          result = await handleDirectDebitConfirm(res.clientSecret ?? clientSecret, paymentMethod?.id)
          break
        case PaymentMethodType.CreditCard:
        default:
          result = await handleCreditCardConfirm(res.clientSecret ?? clientSecret, paymentMethod?.id)
          break
      }

      if (
        result.paymentIntent?.status === 'succeeded' ||
        // act as a success for direct debit
        (paymentMethodType === PaymentMethodType.DirectDebit && result.paymentIntent?.status === 'processing')
      ) {
        nextStep()
        return
      }

      if (result.error) {
        handleStripeError(result.error)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <VStack
      as="form"
      onSubmit={(e) => {
        e.preventDefault()

        const elements = Object.keys(e.target)

        for (const element of elements) {
          if (
            e.target[element] instanceof HTMLElement &&
            (e.target[element].getAttribute('aria-invalid') === 'true' ||
              (e.target[element].getAttribute('aria-required') === 'true' &&
                !(e.target[element] as HTMLInputElement).value))
          ) {
            e.target[element].scrollIntoView()
            return
          }
        }

        handleSubmit()
      }}
      gap="16px"
      w="100%"
    >
      <VStack w="100%" gap="12px">
        {isThreeStepForm && <DonationStepTabs />}
        <DonationBackHeader
          isLoading={isSubmitting}
          title={`${PAYMENT_METHOD_LABEL_MAP[paymentMethodType]} Payment`}
          withLockIcon={true}
          hasBackButton={hasBackButton}
        />
        {hasStripeLoaded && !isThreeStepForm && <NameForm />}
        {hasStripeLoaded && !isThreeStepForm && <ExtraFields fields={extraFields} Components={{ ProfileSelector }} />}
        <Box w="100%">
          <PaymentElement
            id="payment-element"
            options={{
              ...(givingFrequency === DonationFrequency.OneTime && !withAccordion
                ? { wallets: { applePay: 'never', googlePay: 'never' } }
                : { layout: 'accordion', paymentMethodOrder: ['google_pay', 'apple_pay', 'card'] }),
              fields: {
                billingDetails: { address: { country: 'never' }, email: 'never', name: 'never' },
              },
              defaultValues: {
                billingDetails: {
                  email: formValues?.extraFields?.email,
                },
              },
            }}
          />
          {message && <Body2 color="ge.text.danger">{message}</Body2>}
        </Box>
      </VStack>
      <PageButton
        id="donate-checkout"
        data-testid="donate-checkout"
        type="submit"
        name="Primary"
        buttonText={purchaseCopy(
          `Donate ${givingFrequency !== DonationFrequency.OneTime ? `${DONATION_FREQUENCY_TO_LABEL[givingFrequency]} ` : ''}- $${toDisplayAmount(isPayByInstallments ? installmentAmount : totalAmount)}`,
        )}
        isLoading={!hasStripeLoaded || isSubmitting}
        width="100%"
      />
      <TermsAndConditions legalUrl={legalUrl} />
    </VStack>
  )
}

export default DonationCheckoutForm
